import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import CloseIcon from "@mui/icons-material/CloseRounded";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import SideNav from "../../SideNav";
import { useTheme } from "../../theme/ThemeContext";
import { selectUser } from "../../features/userSlice";

export const SideNavigation = ({ showSidebar, setShowSidebar }) => {
  const { isDark, isGlass } = useTheme();
  const user = useSelector(selectUser);

  // Escape closes the drawer. The nav's search box eats Escape first when it
  // has text, so one press clears the search and a second closes the drawer.
  useEffect(() => {
    if (!showSidebar) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setShowSidebar(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [showSidebar, setShowSidebar]);

  const close = () => setShowSidebar(false);

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={close}
        aria-hidden="true"
        className={`fixed inset-0 z-40 bg-[color:var(--app-scrim)] backdrop-blur-[2px] transition-opacity duration-300 ${
          showSidebar ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
      />

      {/* Drawer — a floating glass sheet under Liquid Glass, a full-height
          panel under Classic. */}
      <aside
        aria-label="Navigation menu"
        className={`app-drawer fixed z-50 flex flex-col border shadow-2xl transition-[transform,opacity,visibility] duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] ${
          isGlass
            ? "app-glass-rim bottom-2 left-2 top-2 w-[calc(100%-1rem)] max-w-xs rounded-[1.75rem] backdrop-blur-2xl backdrop-saturate-150 sm:max-w-sm"
            : "bottom-0 left-0 top-0 w-full max-w-xs border-y-0 border-l-0 sm:max-w-sm"
        } ${
          showSidebar
            ? "translate-x-0 opacity-100"
            : // `invisible` (applied once the slide-out finishes) also takes
              // the closed drawer's links out of the tab order.
              "pointer-events-none invisible -translate-x-[110%] opacity-0"
        }`}
      >
        <div className="flex items-center justify-between px-4 pb-2 pt-4">
          <Link to="/" onClick={close} aria-label="Home" className="rounded-lg">
            <img
              src={
                isDark
                  ? require("../../assets/NodeLogo2.png")
                  : require("../../assets/NodeLogoDark.png")
              }
              alt="Big C Node"
              className="h-7 w-auto"
            />
          </Link>
          <button
            onClick={close}
            aria-label="Close navigation menu"
            className="app-icon-btn h-9 w-9"
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-2 py-2">
          <SideNav setShowSidebar={setShowSidebar} />
        </div>

        {/* Who's signed in, with the two places people look for next. */}
        {user && (
          <div className="flex items-center gap-3 border-t border-[color:var(--app-divider)] px-4 py-3">
            <Link
              to="/profile"
              onClick={close}
              className="flex min-w-0 flex-1 items-center gap-3 rounded-xl p-1 transition-colors hover:bg-[color:var(--app-hover)]"
            >
              <span className="h-9 w-9 shrink-0 overflow-hidden rounded-full bg-slate-300 dark:bg-slate-700">
                <img
                  src={`/api/profile/image?id=${user?.id}`}
                  alt=""
                  className="h-full w-full object-cover"
                />
              </span>
              <span className="min-w-0">
                <span className="block truncate text-sm font-semibold">
                  {user.name}
                </span>
                <span className="app-muted block truncate text-xs">
                  {user.branch ? `${user.branch} · ` : ""}View profile
                </span>
              </span>
            </Link>
            <Link
              to="/help"
              onClick={close}
              aria-label="Help & FAQ"
              title="Help & FAQ"
              className="app-icon-btn h-9 w-9 shrink-0"
            >
              <HelpOutlineRoundedIcon fontSize="small" />
            </Link>
          </div>
        )}
      </aside>
    </>
  );
};
